import {
  AppButton,
  AppCheckbox,
  AppCol,
  AppForm,
  AppRadioGroup,
  AppRow,
  AppSelect,
  AppTag,
  AppTextField,
} from '@/components/atoms';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { useTenantConfig } from '@/hooks/admin/useTenantConfig';
import { useTenantSiteTypes } from '@/hooks/admin/useTenantSiteTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import React, { useEffect, useMemo } from 'react';
import { Controller, useWatch, type Control, type SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { getTenantClinicNumberPolicy } from '@/shared/providers/tenantConfig';

type ServiceType = typeof SERVICE_TYPE_A2 | typeof SERVICE_TYPE_R2 | typeof SERVICE_TYPE_BOTH;

/** Build schema; server-side clinic_number error (e.g. duplicate) is injected via apiFieldErrors when present. */
function createClinicSchema(apiFieldErrors?: Record<string, string> | null) {
  return z
    .object({
      site_type: z.string().min(1, 'サイト区分は必須です。'),
      service_type: z
        .enum([SERVICE_TYPE_A2, SERVICE_TYPE_R2, SERVICE_TYPE_BOTH])
        .nullable()
        .refine(val => val !== null, {
          message: 'サビース区分は必須です。',
        }),
      clinic_number: z
        .string()
        .transform(s => s.trim()),
      available_from: z.string().min(1, '有効期限FROMは必須です。'),
      available_to: z.string().nullable().optional(),
      is_excluded_transfer: z.union([z.literal(0), z.literal(1)]),
      is_none_disp_delegation_message: z.union([z.literal(0), z.literal(1)]).optional(),
      is_none_disp_delegation2_message: z.union([z.literal(0), z.literal(1)]).optional(),
      is_none_disp_delegation3_message: z.union([z.literal(0), z.literal(1)]).optional(),
      is_enable_checkin: z.union([z.literal(0), z.literal(1)]).optional(),
      checkin_available_from: z.string().nullable().optional(),
      checkin_available_to: z.string().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.is_enable_checkin === 1) {
        const from = data.checkin_available_from;
        if (from === undefined || from === null || (typeof from === 'string' && !from.trim())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '有効期限FROMは必須です。',
            path: ['checkin_available_from'],
          });
        }
      }

      const siteType = data.site_type;
      const clinicNumber = data.clinic_number;

      if (!clinicNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '治療院IDは必須です。',
          path: ['clinic_number'],
        });
      } else {
        const policy = getTenantClinicNumberPolicy(siteType);
        const invalidCharacters = policy.numericOnly && !/^\d+$/.test(clinicNumber);
        if (invalidCharacters || clinicNumber.length > policy.maxLength) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: policy.numericOnly
              ? `${policy.maxLength}桁以内の数字で入力してください。`
              : `${policy.maxLength}文字以内で入力してください。`,
            path: ['clinic_number'],
          });
        }
      }

      const serverError = apiFieldErrors?.clinic_number;
      if (serverError) {
        // The server's own words, not a fixed string. This used to hardcode
        // 「治療院IDは既に存在します。」 for ANY clinic_number error the API returned, so a
        // 治療院ID clashing with a テナント管理者's ログインID — a different problem with a
        // different fix — was reported as a plain duplicate, and so was every other reason.
        // Only the backend knows which rule refused the value; let it say so.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: serverError,
          path: ['clinic_number'],
        });
      }
    })
    .transform(data => {
      const siteType = data.site_type;
      let clinicNumber = data.clinic_number;

      if (siteType) {
        const policy = getTenantClinicNumberPolicy(siteType);
        if (
          policy.numericOnly &&
          policy.padLength &&
          /^\d+$/.test(clinicNumber) &&
          clinicNumber.length < policy.padLength &&
          clinicNumber.length > 0
        ) {
          clinicNumber = clinicNumber.padStart(policy.padLength, '0');
        }
      }

      return {
        ...data,
        clinic_number: clinicNumber,
      };
    });
}

export interface CreateClinicFormData {
  site_type: string | null;
  service_type: ServiceType | null;
  clinic_number: string;
  available_from: string;
  available_to?: string | null;
  is_excluded_transfer: 0 | 1;
  is_none_disp_delegation_message?: 0 | 1;
  is_none_disp_delegation2_message?: 0 | 1;
  is_none_disp_delegation3_message?: 0 | 1;
  is_enable_checkin?: 0 | 1;
  checkin_available_from?: string | null;
  checkin_available_to?: string | null;
}

interface CreateClinicFormProps {
  initialSiteType?: string;
  fixedSiteType?: string | null;
  /** When set, site-types API returns only family (for admin portal scope). Omit or 'super-admin' for all. */
  siteForSiteTypes?: string;
  allowedSiteTypes?: string[];
  initialValues?: Partial<CreateClinicFormData>;
  onSubmit?: (data: CreateClinicFormData) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  apiFieldErrors?: Record<string, string> | null;
  onClearFieldError?: (field: string) => void;
  hasClinicUsers?: boolean;
  hasLocationA2?: boolean;
  hasLocationR2?: boolean;
}

import { dateValueToIso } from '@/utils/dateConverter';

/** Label for チェックイン有効期限; shows 必須 when チェックイン利用 is checked. */
function CheckinPeriodLabel({ control }: { control: Control<CreateClinicFormData> }) {
  const isEnableCheckin = useWatch({ control, name: 'is_enable_checkin', defaultValue: 0 });
  return (
    <label className="block mb-2">
      チェックイン有効期限
      {isEnableCheckin === 1 && (
        <>
          {' '}
          <AppTag color="magenta">必須</AppTag>
        </>
      )}
    </label>
  );
}

function to01(value: boolean): 0 | 1 {
  return value ? 1 : 0;
}

interface DisableState {
  disableA2: boolean;
  disableR2: boolean;
}

const hasService = (type: ServiceType, target: typeof SERVICE_TYPE_A2 | typeof SERVICE_TYPE_R2) => {
  if (type === SERVICE_TYPE_BOTH) return true;
  return type === target;
};

export const getDisableState = (
  clinicServiceType: ServiceType | null,
  hasClinicUsers = false,
  hasLocationA2 = false,
  hasLocationR2 = false
): DisableState => {
  const clinicHasA2 = clinicServiceType != null && hasService(clinicServiceType, SERVICE_TYPE_A2);
  const clinicHasR2 = clinicServiceType != null && hasService(clinicServiceType, SERVICE_TYPE_R2);

  // clinic_users exist → disable ALL (no add, no remove)
  if (hasClinicUsers) {
    return { disableA2: true, disableR2: true };
  }

  return {
    // clinic_location A2 exists → disable A2
    disableA2: clinicHasA2 && hasLocationA2,
    // clinic_location R2 exists → disable R2
    disableR2: clinicHasR2 && hasLocationR2,
  };
};

function calculateServiceType(a2Checked: boolean, r2Checked: boolean): ServiceType | null {
  if (a2Checked && r2Checked) return SERVICE_TYPE_BOTH;
  if (a2Checked) return SERVICE_TYPE_A2;
  if (r2Checked) return SERVICE_TYPE_R2;
  return null;
}

function getServiceTypeCheckboxStates(serviceType: ServiceType | null) {
  return {
    a2Checked: serviceType === SERVICE_TYPE_A2 || serviceType === SERVICE_TYPE_BOTH,
    r2Checked: serviceType === SERVICE_TYPE_R2 || serviceType === SERVICE_TYPE_BOTH,
  };
}

const CreateClinicForm: React.FC<CreateClinicFormProps> = ({
  onSubmit,
  isLoading = false,
  initialSiteType,
  fixedSiteType,
  siteForSiteTypes,
  allowedSiteTypes,
  initialValues,
  onCancel,
  apiFieldErrors,
  onClearFieldError,
  hasClinicUsers = false,
  hasLocationA2 = false,
  hasLocationR2 = false,
}) => {
  const normalizedFixedSiteType = fixedSiteType?.trim() ? fixedSiteType.trim() : null;
  const isEdit = !!initialValues; // Determine edit mode if initialValues are provided

  const schema = useMemo(() => createClinicSchema(apiFieldErrors), [apiFieldErrors]);
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    clearErrors,
    reset,
    formState: { errors },
  } = useTrimmedForm<CreateClinicFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      site_type: normalizedFixedSiteType ?? initialSiteType ?? '',
      service_type: null,
      clinic_number: '',
      available_from: '',
      available_to: null,
      is_excluded_transfer: 0,
      is_none_disp_delegation_message: 0,
      is_none_disp_delegation2_message: 0,
      is_none_disp_delegation3_message: 1,
      is_enable_checkin: 0,
      checkin_available_from: null,
      checkin_available_to: null,
      ...initialValues, // Override defaults with initialValues
    },
  });
  const siteType = useWatch({ control, name: 'site_type' });
  const serviceType = useWatch({ control, name: 'service_type' });
  const serviceTypeCheckboxStates = useMemo(
    () => getServiceTypeCheckboxStates(serviceType),
    [serviceType]
  );
  const serviceTypeDisableState = useMemo(
    () => getDisableState(serviceType, hasClinicUsers, hasLocationA2, hasLocationR2),
    [serviceType, hasClinicUsers, hasLocationA2, hasLocationR2]
  );

  // Reset form when initialValues change (important for async data loading)
  useEffect(() => {
    if (initialValues) {
      reset({
        ...initialValues,
        site_type: initialValues.site_type ?? normalizedFixedSiteType ?? initialSiteType ?? '',
      });
    }
  }, [initialValues, reset, normalizedFixedSiteType, initialSiteType]);

  /*
   * undefined = all site types (super-admin create). When set = that site's family only.
   */
  const { data: siteTypesData } = useTenantSiteTypes(siteForSiteTypes);

  const allSiteTypes = siteTypesData?.data?.site_types ?? [];

  const effectiveFixedSiteType = useMemo(() => {
    if (normalizedFixedSiteType) return normalizedFixedSiteType;
    if (allSiteTypes.length === 1) return allSiteTypes[0].site_type;
    return null;
  }, [normalizedFixedSiteType, allSiteTypes]);

  const shouldShowSiteTypeSelect = !effectiveFixedSiteType;

  const siteTypeOptions = useMemo(() => {
    const filtered = allowedSiteTypes
      ? allSiteTypes.filter(t => allowedSiteTypes.includes(t.site_type))
      : allSiteTypes;
    return [
      { label: '未選択', value: '' },
      ...filtered.map(t => ({ label: t.name || t.site_type, value: t.site_type })),
    ];
  }, [allSiteTypes, allowedSiteTypes]);

  useEffect(() => {
    if (!effectiveFixedSiteType) return;

    if (!isEdit) {
      setValue('site_type', effectiveFixedSiteType);
    }
  }, [effectiveFixedSiteType, setValue, isEdit]);

  const { data: tenantConfigResponse } = useTenantConfig(siteType || null);
  const tenantConfig = tenantConfigResponse?.data;

  useEffect(() => {
    if (!tenantConfig) return;

    const siteTypeKey = siteType ?? '';

    // If in edit mode and the site_type has not changed from the initial value (DB value),
    // do not apply presets, as this would overwrite the existing DB data for flags.
    if (isEdit && initialValues?.site_type === siteTypeKey) {
      return;
    }

    if (tenantConfig.config_none_disp_delegation_message) {
      setValue(
        'is_none_disp_delegation_message',
        to01(tenantConfig.config_none_disp_delegation_message.checked)
      );
    }
    if (tenantConfig.config_none_disp_delegation2_message) {
      setValue(
        'is_none_disp_delegation2_message',
        to01(tenantConfig.config_none_disp_delegation2_message.checked)
      );
    }
    if (tenantConfig.config_none_disp_delegation3_message) {
      setValue(
        'is_none_disp_delegation3_message',
        to01(tenantConfig.config_none_disp_delegation3_message.checked)
      );
    }
    if (tenantConfig.config_enable_checkin) {
      setValue('is_enable_checkin', to01(tenantConfig.config_enable_checkin.checked));
    }
  }, [tenantConfig, siteType, setValue, isEdit, initialValues?.site_type]);

  // When server returns clinic_number error, run validation so it appears in form state (schema has superRefine)
  useEffect(() => {
    if (apiFieldErrors?.clinic_number) {
      trigger('clinic_number');
    }
  }, [apiFieldErrors?.clinic_number, trigger]);

  const clinicNumberError = errors.clinic_number?.message;

  const handleValidSubmit: SubmitHandler<CreateClinicFormData> = async formValues => {
    if (!onSubmit) return;

    const payload: CreateClinicFormData = {
      ...formValues,
      available_from: dateValueToIso(formValues.available_from) ?? '',
      available_to: dateValueToIso(formValues.available_to),
      checkin_available_from: dateValueToIso(formValues.checkin_available_from),
      checkin_available_to: dateValueToIso(formValues.checkin_available_to),
    };

    await onSubmit(payload);

    if (typeof formValues.clinic_number === 'string') {
      setValue('clinic_number', formValues.clinic_number.trim());
    }
  };

  const handleInvalidSubmit = async () => {
    const raw = getValues('clinic_number');
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      setValue('clinic_number', trimmed);
      await trigger('clinic_number');
    }
  };

  return (
    <div className="space-y-6">
      {/* Row 1: サイト区分 + 治療院ID */}
      <AppRow gutter={[24, 16]}>
        {shouldShowSiteTypeSelect && (
          <AppCol xs={24} md={12}>
            <div className="min-w-[320px]">
              <label className="block mb-2">
                サイト区分 <AppTag color="magenta">必須</AppTag>
              </label>
              <Controller
                name="site_type"
                control={control}
                render={({ field }) => (
                  <>
                    <AppSelect
                      value={field.value ?? undefined}
                      onChange={value => {
                        field.onChange(value ?? '');
                        clearErrors('site_type');
                      }}
                      options={siteTypeOptions}
                      status={errors.site_type ? 'error' : undefined}
                      className="w-full"
                    />
                    {errors.site_type && (
                      <div className="mt-2 text-[#ff4d4f]">{errors.site_type.message}</div>
                    )}
                  </>
                )}
              />
            </div>
          </AppCol>
        )}

        <AppCol xs={24} md={12}>
          <div className="min-w-[320px]">
            <label className="block mb-2">
              治療院ID <AppTag color="magenta">必須</AppTag>
            </label>
            <AppForm.Item
              validateStatus={clinicNumberError ? 'error' : undefined}
              help={clinicNumberError}
              style={{ marginBottom: 0 }}
            >
              <Controller
                name="clinic_number"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    status={clinicNumberError ? 'error' : undefined}
                    onChange={e => {
                      field.onChange(e);
                      onClearFieldError?.('clinic_number');
                      clearErrors('clinic_number');
                    }}
                  />
                )}
              />
            </AppForm.Item>
          </div>
        </AppCol>
      </AppRow>

      {/* Row 2: サビース区分 + 有効期限 */}
      <AppRow gutter={[24, 16]}>
        <AppCol xs={24} md={12}>
          <div className="min-w-[320px]">
            <label className="block mb-2">
              サビース区分 <AppTag color="magenta">必須</AppTag>
            </label>
            <div className="flex flex-col gap-2">
              <AppCheckbox
                disabled={serviceTypeDisableState.disableA2}
                checked={serviceTypeCheckboxStates.a2Checked}
                onChange={e => {
                  const currentStates = getServiceTypeCheckboxStates(getValues('service_type'));
                  const newA2Checked = e.target.checked;
                  const newR2Checked = currentStates.r2Checked;
                  setValue('service_type', calculateServiceType(newA2Checked, newR2Checked));
                  clearErrors('service_type');
                }}
              >
                A2 (はり・きゅう）
              </AppCheckbox>
              <AppCheckbox
                disabled={serviceTypeDisableState.disableR2}
                checked={serviceTypeCheckboxStates.r2Checked}
                onChange={e => {
                  const currentStates = getServiceTypeCheckboxStates(getValues('service_type'));
                  const newA2Checked = currentStates.a2Checked;
                  const newR2Checked = e.target.checked;
                  setValue('service_type', calculateServiceType(newA2Checked, newR2Checked));
                  clearErrors('service_type');
                }}
              >
                R2 (マッサージ)
              </AppCheckbox>
            </div>
            {errors.service_type && (
              <div className="mt-2 text-[#ff4d4f]">{errors.service_type.message}</div>
            )}
          </div>
        </AppCol>

        <AppCol xs={24} md={12}>
          <div className="min-w-[320px]">
            <label className="block mb-2">
              有効期限 <AppTag color="magenta">必須</AppTag>
            </label>
            <div className="flex gap-2 items-center flex-wrap">
              <Controller
                name="available_from"
                control={control}
                render={({ field }) => (
                  <div className={errors.available_from ? 'rounded border border-[#ff4d4f]' : ''}>
                    <RA2NDatePicker
                      value={field.value ?? ''}
                      onChange={v => {
                        field.onChange(v || '');
                        clearErrors('available_from');
                      }}
                      placeholder=""
                    />
                  </div>
                )}
              />
              <span>から</span>
              <Controller
                name="available_to"
                control={control}
                render={({ field }) => (
                  <div className={errors.available_to ? 'rounded border border-[#ff4d4f]' : ''}>
                    <RA2NDatePicker
                      value={field.value ?? ''}
                      onChange={v => field.onChange(v || null)}
                      placeholder=""
                    />
                  </div>
                )}
              />
            </div>
            {errors.available_from && (
              <div className="mt-2 text-[#ff4d4f]">{errors.available_from.message}</div>
            )}
            {errors.available_to && (
              <div className="mt-2 text-[#ff4d4f]">{errors.available_to.message}</div>
            )}
          </div>
        </AppCol>
      </AppRow>

      {/* Row 3: 振替対象外 + 受領委任レセプト：療養費代理人メッセージ非表示 */}
      <AppRow gutter={[34, 16]}>
        <AppCol xs={24} md={12}>
          <AppRow gutter={[34, 16]}>
            <AppCol xs={24} md={12}>
              <div className="min-w-[260px]">
                <label className="block mb-2">
                  振替対象外 <AppTag color="magenta">必須</AppTag>
                </label>
                <Controller
                  name="is_excluded_transfer"
                  control={control}
                  render={({ field }) => (
                    <AppRadioGroup
                      value={field.value}
                      onChange={e => field.onChange(e.target.value)}
                      options={[
                        { label: 'はい', value: 1 },
                        { label: 'いいえ', value: 0 },
                      ]}
                    />
                  )}
                />
              </div>
            </AppCol>
          </AppRow>
        </AppCol>

        {tenantConfig?.config_none_disp_delegation_message?.show && (
          <AppCol xs={24} md={12} className="!ml-[-4px]">
            <div className="min-w-[360px]">
              <label className="block mb-2">受領委任レセプト：療養費代理人メッセージ非表示</label>
              <Controller
                name="is_none_disp_delegation_message"
                control={control}
                render={({ field }) => (
                  <AppCheckbox
                    checked={field.value === 1}
                    onChange={e => field.onChange(to01(e.target.checked))}
                  />
                )}
              />
            </div>
          </AppCol>
        )}
      </AppRow>

      {/* Row 4: 受領委任レセプト：申請欄の申請者名非表示 + 受領委任レセプト：代理人欄の申請者名非表示 */}
      <AppRow gutter={[24, 16]}>
        <AppCol xs={24} md={12}>
          <div className="min-w-[360px]">
            <label className="block mb-2">受領委任レセプト：申請欄の申請者名非表示</label>
            <Controller
              name="is_none_disp_delegation2_message"
              control={control}
              render={({ field }) => (
                <AppCheckbox
                  checked={field.value === 1}
                  disabled={tenantConfig?.config_none_disp_delegation2_message?.enable === false}
                  onChange={e => field.onChange(to01(e.target.checked))}
                />
              )}
            />
          </div>
        </AppCol>

        <AppCol xs={24} md={12}>
          <div className="min-w-[360px]">
            <label className="block mb-2">受領委任レセプト：代理人欄の申請者名非表示</label>
            <Controller
              name="is_none_disp_delegation3_message"
              control={control}
              render={({ field }) => (
                <AppCheckbox
                  checked={field.value === 1}
                  onChange={e => field.onChange(to01(e.target.checked))}
                />
              )}
            />
          </div>
        </AppCol>
      </AppRow>

      {/* Row 5: チェックイン利用 + チェックイン有効期限 (config_enable_checkin from tenant config) */}
      {(tenantConfig?.config_enable_checkin?.show !== false || !tenantConfig) && (
        <AppRow gutter={[24, 16]}>
          <AppCol xs={24} md={12}>
            <div className="min-w-[320px]">
              <label className="block mb-2">チェックイン利用</label>
              <Controller
                name="is_enable_checkin"
                control={control}
                render={({ field }) => (
                  <AppCheckbox
                    checked={field.value === 1}
                    disabled={tenantConfig?.config_enable_checkin?.enable === false}
                    onChange={e => field.onChange(to01(e.target.checked))}
                  />
                )}
              />
            </div>
          </AppCol>

          <AppCol xs={24} md={12}>
            <div className="min-w-[320px]">
              <CheckinPeriodLabel control={control} />
              <div className="flex gap-2 items-center flex-wrap">
                <Controller
                  name="checkin_available_from"
                  control={control}
                  render={({ field }) => (
                    <div
                      className={
                        errors.checkin_available_from ? 'rounded border border-[#ff4d4f]' : ''
                      }
                    >
                      <RA2NDatePicker
                        value={field.value ?? ''}
                        onChange={v => field.onChange(v || null)}
                        placeholder=""
                      />
                    </div>
                  )}
                />
                <span>から</span>
                <Controller
                  name="checkin_available_to"
                  control={control}
                  render={({ field }) => (
                    <div
                      className={
                        errors.checkin_available_to ? 'rounded border border-[#ff4d4f]' : ''
                      }
                    >
                      <RA2NDatePicker
                        value={field.value ?? ''}
                        onChange={v => field.onChange(v || null)}
                        placeholder=""
                      />
                    </div>
                  )}
                />
              </div>
              {errors.checkin_available_from && (
                <div className="mt-2 text-[#ff4d4f]">{errors.checkin_available_from.message}</div>
              )}
              {errors.checkin_available_to && (
                <div className="mt-2 text-[#ff4d4f]">{errors.checkin_available_to.message}</div>
              )}
            </div>
          </AppCol>
        </AppRow>
      )}

      {/* Row 6: Button submit */}
      <div className="mt-6 flex gap-4">
        <AppButton
          type="primary"
          onClick={handleSubmit(handleValidSubmit, handleInvalidSubmit)}
          loading={isLoading}
        >
          更新
        </AppButton>
        {onCancel && <AppButton onClick={onCancel}>詳細画面へ戻る</AppButton>}
      </div>
    </div>
  );
};

export default CreateClinicForm;
